// Mock global-search index for the ⌘K palette (rcm-27, 00-ds-09 §5, D148).
// UI-only: in production this is a per-practice server index over patient name /
// DOB / member ID / claim number / check number / trace / amount / document
// type (§8). Here it's a small in-memory sample so the palette feels real.

import { PATIENTS } from '@/features/patients/directory/patientsMock';

export type SearchKind =
  | 'Patients'
  | 'Claims'
  | 'Payments'
  | 'Documents'
  | 'Appeals'
  | 'Pre-auths'
  | 'Checks and deposits';

/** Kinds in display order (rcm-27 §3). */
export const SEARCH_KINDS: SearchKind[] = [
  'Patients',
  'Claims',
  'Payments',
  'Documents',
  'Appeals',
  'Pre-auths',
  'Checks and deposits',
];

export interface SearchRecord {
  id: string;
  kind: SearchKind;
  /** The row's leading text (a name, a sentence, or a mono claim number). */
  title: string;
  /** Render the title in mono (claim numbers). */
  titleMono?: boolean;
  sub: string;
  /** Right-aligned mono status/amount, e.g. "Paid · $486.00". */
  right?: string;
  /** Where the row opens — the record at its own URL. */
  to: string;
  /** The search haystack: names, numbers, types — never dollar displays. */
  text: string;
  /** Exact amount this record carries, for amount search. */
  amount?: number;
}

/** "See all" opens that kind's history, filtered by the query (rcm-27 §3). */
export const SEARCH_SEE_ALL: Record<SearchKind, string> = {
  Patients: '/patients',
  Claims: '/claims/all',
  Payments: '/posting',
  Documents: '/document-center',
  Appeals: '/denied',
  'Pre-auths': '/pre-auths',
  'Checks and deposits': '/document-center',
};

/** The directory record for a name: search results open it at its own /patients/:id. */
function patientTo(name: string): string {
  const hit = PATIENTS.find((p) => p.name === name);
  return hit ? `/patients/${hit.id}` : '/patients';
}

export const SEARCH_INDEX: SearchRecord[] = [
  // Patients → /patients/:id
  { id: 'p1', kind: 'Patients', title: 'James Whitfield', sub: 'DOB May 2, 1984 · Aetna · next visit Oct 20', to: patientTo('James Whitfield'), text: 'james whitfield 05/02/1984 may 2 1984 aetna w281-44-0917' },
  { id: 'p2', kind: 'Patients', title: 'Sana Malik', sub: 'DOB Jan 19, 1991 · MetLife · next visit Oct 24', to: patientTo('Sana Malik'), text: 'sana malik 01/19/1991 jan 19 1991 metlife' },
  { id: 'p3', kind: 'Patients', title: 'Tomás Vega', sub: 'DOB Sep 8, 1977 · Cigna', to: patientTo('Tomás Vega'), text: 'tomas vega 09/08/1977 sep 8 1977 cigna u8810-2219' },
  { id: 'p4', kind: 'Patients', title: 'Jorge Martinez', sub: 'DOB Mar 3, 1980 · Delta Dental', to: patientTo('Jorge Martinez'), text: 'jorge martinez 03/03/1980 mar 3 1980 delta dental' },
  { id: 'p5', kind: 'Patients', title: 'Ethan Brooks', sub: 'DOB Mar 30, 2002 · Delta Dental', to: patientTo('Ethan Brooks'), text: 'ethan brooks 03/30/2002 mar 30 2002 delta dental' },

  // Claims → /claims/record/:id (mono claim number, right = state · amount)
  { id: 'c1', kind: 'Claims', title: 'CLM-26688', titleMono: true, sub: 'Tomás Rossi · Root canal', right: 'Paid · $819.00', amount: 819, to: '/claims/record/CLM-26688', text: 'clm-26688 26688 tomas rossi root canal d3330 cigna' },
  { id: 'c2', kind: 'Claims', title: 'CLM-26568', titleMono: true, sub: 'Ethan Johnson · Composite filling', right: 'Denied · appeal sent', to: '/claims/record/CLM-26568', text: 'clm-26568 26568 ethan johnson composite filling d2392 guardian' },
  { id: 'c3', kind: 'Claims', title: 'CLM-27319', titleMono: true, sub: 'Chloe Rossi · Root canal', right: 'Paid short · $391.00', amount: 391, to: '/claims/record/CLM-27319', text: 'clm-27319 27319 chloe rossi root canal d3330 unitedhealthcare' },
  { id: 'c4', kind: 'Claims', title: 'CLM-28090', titleMono: true, sub: 'Victor Patel · Composite filling', right: 'Denied', to: '/claims/record/CLM-28090', text: 'clm-28090 28090 victor patel composite filling d2392 aetna' },

  // Payments → Posting › Posted
  { id: 'y1', kind: 'Payments', title: 'Delta paid $486.00 for Jorge Martinez', sub: 'Trace 004417820', right: 'ERA Oct 8 · posted', amount: 486, to: '/posting/posted/PD-JORGE', text: 'delta jorge martinez trace 004417820 payment era' },
  { id: 'y2', kind: 'Payments', title: 'Cigna paid $380.00 for Ethan Walsh', sub: 'Trace 1T772044118', right: 'ERA Oct 13 · to post', amount: 380, to: '/posting/PO-2', text: 'cigna ethan walsh trace 1t772044118 payment era' },

  // Documents → Document Center
  { id: 'd1', kind: 'Documents', title: 'EOB · Bluebonnet Dental Plan', sub: 'received by fax · 2 amounts unreadable', right: 'needs a person', to: '/document-center/DOC-BB', text: 'eob bluebonnet dental plan fax unreadable' },
  { id: 'd2', kind: 'Documents', title: 'Virtual card · Cigna', sub: 'Mark Ellis, Diego Ramos · expires Dec 11, 2026', right: '$318.40', amount: 318.4, to: '/document-center/cards/CARD-CIGNA-318', text: 'virtual card cigna mark ellis diego ramos' },

  // Appeals → Denied & underpaid
  { id: 'a1', kind: 'Appeals', title: 'Appeal · CLM-0611 · Aetna', sub: 'Rachel Stone · sent Jul 28', right: 'Aetna kept its denial', to: '/denied/appeals/CLM-0611', text: 'appeal clm-0611 0611 aetna rachel stone' },
  { id: 'a2', kind: 'Appeals', title: 'Appeal · CLM-0818 · Aetna', sub: 'Linda Okafor · R-26-55810', right: 'appeal ready', to: '/denied/CLM-0818', text: 'appeal clm-0818 0818 aetna linda okafor r-26-55810 55810' },

  // Pre-auths → Pre-auths
  { id: 'pr1', kind: 'Pre-auths', title: 'Pre-auth · Teresa Nguyen', sub: 'MetLife · implant #19', right: 'to send', to: '/pre-auths/PA-T1', text: 'pre-auth preauth teresa nguyen implant d6010 metlife' },
  { id: 'pr2', kind: 'Pre-auths', title: 'Pre-auth · Olivia Grant', sub: 'Delta · implant #30 · DEL-PA-30881', right: 'asked for more', to: '/pre-auths/PA-S2', text: 'pre-auth preauth olivia grant implant d6010 delta del-pa-30881' },

  // Checks and deposits → Document Center › Checks / Posting › Bank
  { id: 'k1', kind: 'Checks and deposits', title: 'Delta check 004417820 · $3,486.20', sub: 'Delta Dental of Texas · 9 claims', right: 'received today', amount: 3486.2, to: '/document-center/checks', text: 'delta dental check 004417820 deposit' },
  { id: 'k2', kind: 'Checks and deposits', title: 'Delta Dental deposit · $88.00', sub: 'Chase ··4417 · Oct 13', right: 'matched', amount: 88, to: '/posting/matched/MD-20261013-deltadental', text: 'delta dental deposit chase 4417 1t0925230 matched' },
];

/** The last records the user opened (empty-query "Recent"). In-memory mock. */
let recents: SearchRecord[] = [0, 5, 9, 16, 14]
  .map((i) => SEARCH_INDEX[i])
  .filter((r): r is SearchRecord => r != null);

export function getRecents(): SearchRecord[] {
  return recents.slice(0, 5);
}

/** Record that a row was opened — moves it to the front of Recent. */
export function pushRecent(rec: SearchRecord): void {
  recents = [rec, ...recents.filter((r) => r.id !== rec.id)].slice(0, 5);
}

/** "486", "486.00", "$486" → 486; "48" → 48; non-amounts → null. */
export function parseAmount(q: string): number | null {
  const cleaned = q.replace(/[$,\s]/g, '');
  if (/^\d+(\.\d{1,2})?$/.test(cleaned)) return Number(cleaned);
  return null;
}

/**
 * Client-side filter over the mock index (production: server, §8). Text matches
 * are substring over the haystack (names, numbers, types); amounts match
 * exactly ("486"/"486.00"/"$486" → $486.00, but "48" never does).
 */
export function searchRecords(query: string): SearchRecord[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [];
  const amt = parseAmount(q);
  return SEARCH_INDEX.filter((r) => {
    if (r.text.toLowerCase().includes(q)) return true;
    if (amt !== null && r.amount != null && r.amount === amt) return true;
    return false;
  });
}
